import { ReactNode } from 'react';

export function ShieldIcon({
  size = 14,
  color = 'var(--sm-coral)',
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 2.5L4.5 5.5V11c0 5.2 3.2 9.3 7.5 10.5 4.3-1.2 7.5-5.3 7.5-10.5V5.5L12 2.5z"
        stroke={color}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M8.7 12.1l2.1 2.1 4.2-4.4"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.1 24.5c0-1.6-.1-3.1-.4-4.6H24v9h11.9c-.5 2.8-2.1 5.2-4.4 6.8v5.6h7.1c4.2-3.9 6.5-9.6 6.5-16.8z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.9 0 10.9-2 14.5-5.3l-7.1-5.6c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8.1 41 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.8 28.2c-.4-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.7H4.5C3 17 2 20.4 2 24s1 7 4.5 9.9l7.3-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.7c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.2 29.9 2 24 2 15.4 2 8.1 7 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9.1 12.2-9.1z"
      />
    </svg>
  );
}

export function SoulMapWordmark() {
  const dots = [
    { x: 2, y: 14, r: 2.6, c: '#8C9CEE' },
    { x: 9, y: 6, r: 2.2, c: '#EE8571' },
    { x: 17, y: 3, r: 3, c: '#F5AB9C' },
    { x: 25, y: 7, r: 2.2, c: '#8C9CEE' },
    { x: 30, y: 15, r: 2.6, c: '#EE8571' },
  ];

  return (
    <div className="flex items-center gap-2.5">
      <svg width="32" height="20" viewBox="0 0 32 20" aria-hidden="true">
        {dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={d.r} fill={d.c} />
        ))}
      </svg>
      <span
        className="sm-serif"
        style={{ fontSize: 20, fontWeight: 500, color: 'var(--sm-ink)' }}
      >
        Soul<span style={{ fontWeight: 700 }}>Map</span>
      </span>
    </div>
  );
}

/** Wordmark, Mentora Institute and the account menu, shared by every screen. */
export function SoulMapTopBar({ account }: { account?: ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-6 py-6 md:px-12">
      <SoulMapWordmark />
      <div className="flex min-w-0 items-center gap-4">
        <span className="sm-eyebrow hidden md:block">Mentora Institute</span>
        {account}
      </div>
    </div>
  );
}

export const SOUL_MAP_ARC_LABELS = [
  'Code',
  'Circumstances',
  'Choices',
  'Context',
] as const;

const CONSTELLATION_VIEW_WIDTH = 460;
const CONSTELLATION_VIEW_HEIGHT = 130;
const CONSTELLATION_PAD_X = 72;
const ARC_END_Y = 58;
const ARC_PEAK_Y = 16;
const ARC_DOT_RADIUS = 5;
const ARC_LENGTH_SAMPLES = 120;

type Point = { x: number; y: number };

function getArcEndpoints() {
  const xStart = CONSTELLATION_PAD_X;
  const xEnd = CONSTELLATION_VIEW_WIDTH - CONSTELLATION_PAD_X;
  return { xStart, xEnd };
}

function cubicPointAtT(
  t: number,
  xStart: number,
  xEnd: number,
  endY: number,
  peakY: number
): Point {
  const u = 1 - t;
  const x =
    u * u * u * xStart +
    3 * u * u * t * xStart +
    3 * u * t * t * xEnd +
    t * t * t * xEnd;
  const y =
    u * u * u * endY +
    3 * u * u * t * peakY +
    3 * u * t * t * peakY +
    t * t * t * endY;

  return { x, y };
}

function cubicArcLength(
  xStart: number,
  xEnd: number,
  endY: number,
  peakY: number
): number {
  let length = 0;
  let previous = cubicPointAtT(0, xStart, xEnd, endY, peakY);

  for (let index = 1; index <= ARC_LENGTH_SAMPLES; index += 1) {
    const current = cubicPointAtT(
      index / ARC_LENGTH_SAMPLES,
      xStart,
      xEnd,
      endY,
      peakY
    );
    length += segmentLength(previous, current);
    previous = current;
  }

  return length;
}

function segmentLength(from: Point, to: Point): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}

function sampleCubicAtArcLength(
  targetDistance: number,
  xStart: number,
  xEnd: number,
  endY: number,
  peakY: number
): Point {
  let accumulated = 0;
  let previous = cubicPointAtT(0, xStart, xEnd, endY, peakY);

  for (let index = 1; index <= ARC_LENGTH_SAMPLES; index += 1) {
    const current = cubicPointAtT(
      index / ARC_LENGTH_SAMPLES,
      xStart,
      xEnd,
      endY,
      peakY
    );
    const segment = segmentLength(previous, current);

    if (
      accumulated + segment >= targetDistance ||
      index === ARC_LENGTH_SAMPLES
    ) {
      const ratio =
        segment === 0 ? 0 : (targetDistance - accumulated) / segment;
      return {
        x: previous.x + (current.x - previous.x) * ratio,
        y: previous.y + (current.y - previous.y) * ratio,
      };
    }

    accumulated += segment;
    previous = current;
  }

  return cubicPointAtT(1, xStart, xEnd, endY, peakY);
}

/** Equal arc-length spacing along one smooth cubic; middle nodes sit higher on the curve. */
function buildArcGeometry(count: number) {
  const { xStart, xEnd } = getArcEndpoints();
  const totalLength = cubicArcLength(xStart, xEnd, ARC_END_Y, ARC_PEAK_Y);

  const points = Array.from({ length: count }, (_, index) => {
    const distance =
      count === 1 ? totalLength / 2 : (index / (count - 1)) * totalLength;
    return sampleCubicAtArcLength(
      distance,
      xStart,
      xEnd,
      ARC_END_Y,
      ARC_PEAK_Y
    );
  });

  const path = `M ${xStart} ${ARC_END_Y} C ${xStart} ${ARC_PEAK_Y}, ${xEnd} ${ARC_PEAK_Y}, ${xEnd} ${ARC_END_Y}`;

  return { points, path };
}

export function getSoulMapArcIndex(label: string): number {
  const normalized = label.trim().toLowerCase();
  return SOUL_MAP_ARC_LABELS.findIndex(
    (arc) => arc.toLowerCase() === normalized
  );
}

export function ConstellationPath({
  activeIndex = -1,
  labels = [...SOUL_MAP_ARC_LABELS],
}: {
  activeIndex?: number;
  labels?: string[];
}) {
  const { points, path } = buildArcGeometry(labels.length);

  return (
    <svg
      viewBox={`0 0 ${CONSTELLATION_VIEW_WIDTH} ${CONSTELLATION_VIEW_HEIGHT}`}
      className="w-full max-w-lg mx-auto"
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke="var(--sm-coral)"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.5"
        className="sm-path-line"
      />
      {points.map((p, i) => (
        <g key={labels[i] ?? i}>
          <circle
            cx={p.x}
            cy={p.y}
            r={ARC_DOT_RADIUS}
            fill={
              activeIndex === i
                ? 'var(--sm-coral)'
                : activeIndex > i
                  ? 'var(--sm-coral-soft)'
                  : '#fff'
            }
            stroke="var(--sm-coral)"
            strokeWidth="1.4"
          />
          {labels[i] ? (
            <text
              x={p.x}
              y={p.y + 26}
              textAnchor="middle"
              fill="var(--sm-muted-2)"
              fontSize="11"
              fontFamily="Fraunces, Georgia, serif"
              fontWeight={400}
            >
              {labels[i]}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

export function BackgroundConstellation() {
  const stars = [
    [8, 12],
    [22, 30],
    [40, 8],
    [63, 22],
    [80, 40],
    [15, 55],
    [55, 60],
    [90, 15],
    [35, 45],
    [70, 5],
  ];

  return (
    <svg
      viewBox="0 0 100 70"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full opacity-0 sm-fade-up"
      style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
      aria-hidden="true"
    >
      {stars.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={i % 3 === 0 ? 0.4 : 0.22}
          fill="var(--sm-coral-soft)"
          opacity={0.55}
        />
      ))}
    </svg>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="sm-btn-primary"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="sm-btn-secondary"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
